import React, { createContext, useState, ReactNode } from "react"
import { EditorState } from 'draft-js';
import axios from './axiosConfig'
import { pick, omit } from 'lodash'
import { AccountInfo, AuthError } from '@azure/msal-browser'
import UserData from './Interfaces/UserData'
import Envelope from './Interfaces/Envelope'
import ContextType from './Interfaces/ContextType'
import UserProps from './Interfaces/UserProps'
import querystring from 'querystring'
import moment from 'moment-timezone'
import Cookies from 'js-cookie'

export const EmpdContext = createContext<ContextType>({
    authUser: {
        homeAccountId: '', 
        environment: '', 
        tenantId: '', 
        username: '', 
        localAccountId: '',
    },
    setAuthUser: () => {},
    isLoading: true,
    setIsLoading: () => {},
	isPatientLoading: false,
	setIsPatientLoading: () => {},
    isRedirecting: false,
    setIsRedirecting: () => {},
    patientResult: {},
    setPatientResult: () => {},
    loadingMessage: undefined,
    setLoadingMessage: () => {},
    errorState: false,
    setErrorState: () => {},
    errorMessage: undefined,
    setErrorMessage: () => {},
    updateFields: undefined,
    setUpdateFields: () => {},
    userCreated: false,
    setUserCreated: () => {},
    changesMade: false,
    setChangesMade: () => {},
    fetchPatientData: () => {},
    addNewUser: () => {},
    createRedcapEntry: () => {},
    storeConsent: () => {},
    login: () => Promise.resolve(null),
    userData: null,
    setUserData: () => {},
    getCookie: () => {},
    writePatientData: () => {},
    activeMrn: null,
    setActiveMrn: () => {},
    writeComplete: () => {},
    fetchClinicalTrials: () => {},
    clinicalTrials: null,
    setClinicalTrials: () => {},
    countClinicalTrials: null,
    setCountClinicalTrials: () => {},
    hasBeenDiagnosed: false,
    setHasBeenDiagnosed: () => {},
    activeSession: false,
    setActiveSession: () => {},
    envelope: null,
    setEnvelope: () => {},
    pdfDoc: null,
    setPdfDoc: () => {},
    isResearchParticipant: false,
    setIsResearchParticipant: () => {},
    writeFlags: () => {},
    fetchNonParticipantData: () => {},
    selectedCategory: null,
    setSelectedCategory: () => {},
    mergeData: () => {},
    sign: () => {},
    fetchBioOptions: () => {},
    providerInfo: undefined,
    fetchProviderInfo: () => {},
    providerId: undefined,
    setProviderId: () => {},
	isUsPerson: true,
	setIsUsPerson: () => {},
	communicate: true,
	setCommunicate: () => {},
	openWelcomeDialog: false,
	setWelcomeDialogOpen: () => {},
	openDeclinedDialog: false,
	setDeclinedDialogOpen: () => {},
	schemaName: undefined,
	setSchemaName: () => {},
	demoTable: undefined,
	setDemoTable: () => {},
	careTable: undefined,
	setCareTable: () => {},
	idField: undefined,
	setIdField: () => {},
	eventName: undefined,
	setEventName: () => {},
	fetchNamespace: () => {},
	conditionSet: undefined,
	setConditionSet: () => {},
	editorState: EditorState.createEmpty(),
	setEditorState: () => {},
	openEditDialog: false,
	setEditDialogOpen: () => {},
	fetchMessageContent: () => {},
	writeMessageContent: () => {},
	fetchProfile: () => {},
	user: undefined,
	setUser: () => {},
	authError: undefined,
	setAuthError: () => {},
	openAuthErrorDialog: false,
	setAuthErrorDialogOpen: () => {},
	messagingContent: undefined,
	setMessagingContent: () => {},
})

interface ContextProviderProps {
    children: ReactNode,
}

const categories = [
    'General Demography',
    'Medical History',
    'Disease',
    'Social Determinants',
    'Where Care Is Delivered',
]

const providerCategories = [
    'Surgical',
    'Labs',
    'Genomics PDF_Report',
    'Genomics PDF_Genomic',
    'Genomics_Biomarkers',
    'Genomics Data Model_Germline',
    'Genomics Data Model_Somatic',
    'Genomics VCF_Variant',
    'Genomics VCF_Transcript',
    'Genomics VCF_Gene Variant',
    'Genomics VCF_Star Allele',
    'Radiological',
    'Treatment Radiation',
    'Treatment Chemotherapy',
    'Wound Care-Healing',
]

export const EmpdProvider: React.FC<ContextProviderProps> = ({ children }) => {
    const [authUser, setAuthUser] = useState<AccountInfo>({
        homeAccountId: '',
        environment: '',
        tenantId: '',
        username: '',
        localAccountId: '',
    })

    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [isPatientLoading, setIsPatientLoading] = useState<boolean>(false)
    const [isRedirecting, setIsRedirecting] = useState<boolean>(false)
    const [loadingMessage, setLoadingMessage] = useState<string|undefined>(undefined)
    const [errorState, setErrorState] = useState<boolean>(false)
    const [errorMessage, setErrorMessage] = useState<object|undefined>(undefined)
    const [patientResult, setPatientResult] = useState<any>({})
    const [changesMade, setChangesMade] = useState<boolean>(false)
    const [updateFields, setUpdateFields] = useState<any>({})
    const [userData, setUserData] = useState<UserData | null>(null)
    const [activeMrn, setActiveMrn] = useState<string | null>(null)
    const [clinicalTrials, setClinicalTrials] = useState<any | null>(null)
    const [countClinicalTrials, setCountClinicalTrials] = useState<number | null>(null)
    const [isResearchParticipant, setIsResearchParticipant] = useState<boolean>(false)
    const [hasBeenDiagnosed, setHasBeenDiagnosed] = useState<boolean>(false)
    const [activeSession, setActiveSession] = useState<boolean>(false)
    const [envelope, setEnvelope] = useState<Envelope | null>(null)
    const [pdfDoc, setPdfDoc] = useState<string | null>(null)
    const [userCreated, setUserCreated] = useState<boolean>(false)
    const [selectedCategory, setSelectedCategory] = useState<any | null>(null)
    const [providerInfo, setProviderInfo] = useState<any | undefined>(undefined)
    const [providerId, setProviderId] = useState<string | undefined>(undefined)
    const [isUsPerson, setIsUsPerson] = useState<boolean>(true)
    const [communicate, setCommunicate] = useState<boolean>(true)
	const [openWelcomeDialog, setWelcomeDialogOpen] = useState<boolean>(false)
	const [openDeclinedDialog, setDeclinedDialogOpen] = useState<boolean>(false)
	const [schemaName, setSchemaName] = useState<string|undefined>(undefined)
    const [demoTable, setDemoTable] = useState<string|undefined>(undefined)
    const [careTable, setCareTable] = useState<string|undefined>(undefined)
    const [idField, setIdField] = useState<string|undefined>(undefined)
	const [conditionSet, setConditionSet] = useState<number|undefined>(undefined)
	const [eventName, setEventName] = useState<string|undefined>(undefined)
	const [editorState, setEditorState] = useState<EditorState>(EditorState.createEmpty())
	const [openEditDialog, setEditDialogOpen] = useState<boolean>(false)
	const [user, setUser] = useState<any|undefined>(undefined)
	const [authError, setAuthError] = useState<string|undefined>(undefined)
	const [openAuthErrorDialog, setAuthErrorDialogOpen] = useState<boolean>(false)
	const [messagingContent, setMessagingContent] = useState<any|undefined>(undefined)
	
    const getCookie = async(redirectUri:string) => {
        if( userData === null ) {
            return 
        }

        const query = {
            redirect_uri: `${window.location.origin}/${redirectUri}`
        }

        try {
            await axios.get(`/auth/shibboleth?${querystring.stringify(query)}`,{
                headers: {
                    mail: userData.email
                }
            })

        } catch( error: any ) {
            console.error(error.message)
        }
    }

    const addNewUser = async({type, first_name, last_name, email_address, city, state_name, zipcode, iss, oid, file}: UserProps) => {
        const user = {
            first_name: first_name,
            last_name: last_name,
            email_address: email_address,
            city: city,
            state_name: state_name,
            zipcode: zipcode,
            status: 'Active',
            roles: type === 'patient' ? [4] : [4,1],
            iss: iss,
        }
		const redirectUri = type === 'patient' ? 'registered' : 'provider/dashboard'

        try {
            setIsLoading(true)
            await axios.get('/auth/auth/status')
            setIsLoading(false)
        } catch( error: any ) {
            if( error.status === 401 ) {
                await axios.post('/empd/users/admin/ad', user)
                const query = {
                    redirect_uri: `${window.location.origin}/${redirectUri}`
                }

                await axios.get(`/auth/shibboleth?${querystring.stringify(query)}`,{
                    headers: {
                        mail: email_address
                    }
                })
                setIsLoading(false)
				if( type === 'patient' && oid !== undefined && file !== undefined) {
	                await storeConsent(oid, file)
				}
                return 
            }

            if( error.hasOwnProperty('healirIdRequest') && error.healidRequest.url.match(/heal-id\/api\/id\/createorget/) ){
                return
            } else {
                console.error(error.message)
            }
        }
    }

	const fetchNamespace = async() => {
	    let url = `/empd_query_builder/query/namespace`
        try {
			setIsLoading(true)
            const idData = await axios.get(url)
            setSchemaName(idData.data.schema)
            setDemoTable(idData.data.demoTable)
            setCareTable(idData.data.careTable)
            setIdField(idData.data.id)
			setConditionSet(idData.data.conditionSet)
			setEventName(idData.data.eventName)
			setIsLoading(false)
			return idData.data
        } catch(error) {
            console.error(error)
			setIsLoading(false)
            return
        }
	}

    const createRedcapEntry = async(data: any) => {
		const userClaims = data.idTokenClaims

		const uData = {
        	family_name: userClaims.family_name,
            given_name: userClaims.given_name,
            email: userClaims.emails[0],
            state: userClaims.state,
            country: userClaims.country,
            postalCode: userClaims.postalCode,
            newUser: userClaims.newUser,
            userObjectId: userClaims.oid,
            included: false,
            consented: userClaims.extension_consented ? userClaims.extension_consented : false,
            iss: userClaims.iss
        }

        localStorage.setItem('familyName', userClaims.family_name)
        localStorage.setItem('givenName', userClaims.given_name)
        localStorage.setItem('email', userClaims.emails[0])
        localStorage.setItem('userObjectId', userClaims.oid)
        localStorage.setItem('newUser', userClaims.newUser)
        localStorage.setItem('consented', userClaims.extension_consented ? userClaims.extension_consented : false)

		let conditionSet
		let schemaName
		let demoTable
		let eventName

		try {
            setIsLoading(true)
			let url = `/empd_query_builder/query/namespace`
            const idData = await axios.get(url)
			if( idData && idData.data ) {
				conditionSet = idData.data.conditionSet
				schemaName = idData.data.schemaName
				demoTable = idData.data.demoTable
				eventName = idData.data.eventName
			}
		} catch( error ) {
			console.error(error)
			setIsLoading(false)
			return
		}

		try {
			const url = `/empd_query_builder/query/record/admin?mrn=${userClaims.oid}&iss=${userClaims.iss}&condition_set=${conditionSet}`
    	    const results = await axios.get(url)
			// the user exists, just return
			if( Object.keys(results.data).length > 0 ) {
				setIsLoading(false)
				return
			}
			setWelcomeDialogOpen(true)
		} catch( error ) {
			console.error(`There was an error checking the user status: ${error}`)
			setIsLoading(false)
			return
		}

       	const url = `/empd_query_builder/query/record/admin`
        let fields = {
            [`${schemaName}.${demoTable}.first_name`]: userClaims.given_name,
            [`${schemaName}.${demoTable}.last_name`]: userClaims.family_name,
            [`${schemaName}.${demoTable}.identity`]: userClaims.oid,
            [`${schemaName}.${demoTable}.email`]: userClaims.emails[0],
            [`${schemaName}.${demoTable}.state`]: userClaims.state,
            [`${schemaName}.${demoTable}.country`]: userClaims.country,
            [`${schemaName}.${demoTable}.zip_postal`]: userClaims.postalCode,
        }

	    const update = { 
            mrn: userClaims.oid,
            updateFields: { [`${eventName}`]: fields },
            iss: userClaims.iss
        }
        
        try {
            // Send a PATCH request
            await axios.patch(url, update)
			setUserCreated(true)
        	setUserData(uData)
	        setActiveMrn(userClaims.oid)
            setIsLoading(false)
        } catch( error ) {
            setIsLoading(false)
            console.error(error)
        }
    }

    const writeFlags = async(fields: any) => {
        const url = `/empd_query_builder/query/record/admin`
        if( userData === null ) {
            console.log('no user data')
            return
        }

        const update = {
            mrn: activeMrn,
            updateFields: { [`${eventName}`]: { ...fields}},
            iss: userData.iss
        }

        try {
            setIsLoading(true)
            // Send a PATCH request
            await axios.post(url, update)
            await fetchNonParticipantData()
            setIsLoading(false)
        } catch( error ) {
            setIsLoading(false)
            console.error(error)
        }
    }

    const storeConsent = async(identity: string, file: Blob) => {
        const url = '/genetic_query_builder/query/record/file'
//        const arrayBuffer = await file.arrayBuffer()
 //       const buffer = new Uint8Array(arrayBuffer)

        const formData = new FormData()
        formData.append('mrn', identity)
        formData.append('updateFields', JSON.stringify({
            [`${eventName}`]: {
                [`${schemaName}.${demoTable}.consent_form`]: 1
            }
        }))
        formData.append('file', file, 'signed_consent.pdf')

        try{
            setIsLoading(true)
            await axios.post(url, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                }
            })
            setIsLoading(false)
        } catch( error) {
            console.error(error)
        }
    }

    const fetchNonParticipantData = async() => {
        setLoadingMessage(`Fetching Patient Data...`)
        setIsPatientLoading(true)
        let data: any
/*
		if( Object.keys(patientResult).length > 0 ) {
			setIsPatientLoading(false)
			fetchProfile('registered')
			return
		}
*/
        if( authUser && authUser.hasOwnProperty('account') && authUser.username !== '' ) {
            data = authUser.idTokenClaims
        } else if( localStorage.getItem('authUser') ) {
            const authUserData = localStorage.getItem('authUser')
            if( authUserData ) {
                data = JSON.parse(authUserData).idTokenClaims
            }
        }

        try {
            const url = `/empd_query_builder/query/record/admin?mrn=${data.sub}&iss=${data.iss}&condition_set=${conditionSet}`

            const results = await axios.get(url)
			if( Object.keys(results.data).length === 0 ) {
				setIsPatientLoading(false)
				return
			}
//            const results = await axios.get('../empd_query_builder.json')
            const returnData = {...results.data}
//            Deal with patient not there yet

            const included = returnData.categories['General Demography'].records[0].columns.find((element: any) => element.fullDatabaseFieldName === `${schemaName}.${demoTable}.included`).value === 'True' ? true : false
            const consented = returnData.categories['General Demography'].records[0].columns.find((element: any) => element.fullDatabaseFieldName === `${schemaName}.${demoTable}.consented`).value === 'True' ? true : false

            const mrn = returnData.categories['General Demography'].records[0].columns.find((element: any) => element.fullDatabaseFieldName === `${schemaName}.${demoTable}.${idField}`).value

            returnData.categories = pick(results.data.categories, categories)
			setIsUsPerson(returnData.categories['General Demography'].records[0].columns.find((col: any) => col.field_label.trim() === 'US Person').value)
            setActiveMrn(mrn)
            setPatientResult(returnData)
            setHasBeenDiagnosed(included)
            setIsResearchParticipant(consented)
			setCommunicate(returnData.categories['General Demography'].records[0].columns.find((element: any) => element.fullDatabaseFieldName === `${schemaName}.${demoTable}.communicate`).value)
			setUserCreated(true)
			fetchProfile('registered')
            setIsPatientLoading(false)
        } catch( error ) {
            setPatientResult({})
            setIsPatientLoading(false)
            console.error(error)
        }

        if( !userData ) {
            const authUserString = localStorage.getItem('authUser')
            let aUser: any
            if ( authUserString ) {
               aUser = JSON.parse(authUserString)
            }

            const uData = {
                given_name: localStorage.getItem('givenName') ?? "",
                family_name: localStorage.getItem('familyName') ?? "",
                email: localStorage.getItem('email') ?? "",
                token: "", //searchParams.get('code') ?? "",
                city: localStorage.getItem('city') ?? "",
                userObjectId: localStorage.getItem('userObjectId') ?? "",
                included: localStorage.getItem('included') === 'true' ?? false,
                consented: localStorage.getItem('consented') === 'true' ?? false,
                newUser: localStorage.getItem('newUser') === 'true' ?? false,
                iss: aUser.idTokenClaims.iss,
            }
            setUserData(uData)
        }
    }

    const fetchProviderInfo = async(identity: string | undefined) => {

        if( identity === undefined ) {
            return
        }

        setLoadingMessage(`Fetching Provider Data...`)
        setIsLoading(true)
       
        try {
            const url = `/genetic_query_builder/query/record/condition_set/${conditionSet}`
            const query = {
                "readOnly": false,
                "patientQuery": {
                    "field_name": `${schemaName}.${demoTable}.${idField}`,
                    "operator": "=",
                    "value": identity,
                    "category": "General Demography"
                },
                "descriptiveFields": true,
            }

            const results = await axios.post(url, query)
            //const results = await axios.get('../empd_query_builder.json')
            
            const returnData = {...results.data}
            returnData.categories = pick(results.data.categories, providerCategories)
            setProviderInfo(returnData)
            setIsLoading(false)
        } catch( error ) {
            setProviderInfo({})
            setIsLoading(false)
            console.error(error)
        }
    }

    const fetchPatientData = async(identity: any) => {
        setLoadingMessage(`Fetching Patient Data...`)
        setIsLoading(true)
       
        try {
			const cookieName = 'HealUserId'
			if( !Cookies.get(cookieName) ) {
				await fetchNonParticipantData()
				await getCookie('registered')
			}

            const url = `/genetic_query_builder/query/record/condition_set/${conditionSet}`
            const query = {
                "readOnly": false,
                "patientQuery": {
                    "field_name": `${schemaName}.${demoTable}.${idField}`,
                    "operator": "=",
                    "value": identity,
                    "category": "General Demography"
                },
                "descriptiveFields": true,
            }

            const results = await axios.post(url, query)
            //const results = await axios.get('../empd_query_builder.json')
            
            const returnData = {...results.data}
            returnData.categories = pick(results.data.categories, categories)

			setIsUsPerson(returnData.categories['General Demography'].records[0].columns.find((col: any) => col.field_label.trim() === 'US Person').value)

			const included = returnData.categories['General Demography'].records[0].columns.find((element: any) => element.fullDatabaseFieldName === `${schemaName}.${demoTable}.included`).value === 'True' ? true : false
            const consented = returnData.categories['General Demography'].records[0].columns.find((element: any) => element.fullDatabaseFieldName === `${schemaName}.${demoTable}.consented`).value === 'True' ? true : false

            const mrn = returnData.categories['General Demography'].records[0].columns.find((element: any) => element.fullDatabaseFieldName === `${schemaName}.${demoTable}.${idField}`).value

            setActiveMrn(mrn)
			setUserCreated(true)
            setPatientResult(returnData)
            setHasBeenDiagnosed(included)
            setIsResearchParticipant(consented)
			setCommunicate(returnData.categories['General Demography'].records[0].columns.find((element: any) => element.fullDatabaseFieldName === `${schemaName}.${demoTable}.communicate`).value)
			fetchProfile('registered')

            setIsLoading(false)
        } catch( error ) {
			fetchProfile('registered')
            setPatientResult({})
            setIsLoading(false)
            console.error(error)
        }
    }

    interface Fields {
        [key: string]: any
    }

    const writePatientData = async () => {
        if( Object.keys(updateFields).length === 0 ) {
            fetchPatientData(activeMrn)
            return
        }

        setLoadingMessage('Writing Patient Data...')
        setIsLoading(true)
        setPatientResult({})
        // Create query url
        const url = `/genetic_query_builder/query/record`
        let fields: Fields = {}
//        Object.keys(updateFields).map((fieldName: string)  => {
        Object.keys(updateFields).forEach((fieldName: string)  => {
            // redcap gets mad if date validated fields don't look exactly like YYYY-MM-DD (or Y-M-D in their vocabulary)
            const field = updateFields[fieldName].fieldName as string

            if( moment.isMoment(updateFields[fieldName].value) ) {
                fields[field] = moment(updateFields[fieldName].value).tz('UTC').format('YYYY-MM-DD')
            } else {
                if( updateFields[fieldName].hasOwnProperty('value') ) {
                    if( updateFields[fieldName].value.hasOwnProperty('value') ) {
                        fields[updateFields[fieldName].fieldName] = updateFields[fieldName].value.value
                    } else {
                         fields[updateFields[fieldName].fieldName] = updateFields[fieldName].value
                    }
                } else {
                    fields[fieldName] = updateFields[fieldName]
                }
            }

            // yes / nos look like strings; need to make them booleans on the way back out
            if( updateFields[fieldName].hasOwnProperty['value'] &&  updateFields[fieldName].value.label === 'Yes' ) {
                fields[updateFields[fieldName].fieldName] = true
            }

            if( updateFields[fieldName].hasOwnProperty['value'] && updateFields[fieldName].value.label === 'No' ) {
                fields[updateFields[fieldName].fieldName] = false
            }
        })


        try {
            // Send a PATCH request
            await axios.patch(url, {
                mrn: activeMrn,
                updateFields: { [`${eventName}`]: fields },
            })
            fetchPatientData(activeMrn)
        } catch (error: any) {
            setErrorState(true)
            setIsLoading(false)
            setErrorMessage({
                title: 'There was an issue writing the patient data',
                message: error.message,
            })
            console.error(error)
            setActiveMrn(activeMrn)
        }

        setChangesMade(false)
        setUpdateFields({})
    }

    const writeComplete = async(category: string | null) => {
        setLoadingMessage('Locking category...')
        setIsLoading(true)

        setPatientResult({})
        // Create query url
        const url = `/genetic_query_builder/query/record/complete`

        try {
            // Send a PATCH request
            await axios.patch(url, {
                mrn: activeMrn,
                category: category,
                eventName: eventName
            })

            fetchPatientData(activeMrn)
            setIsLoading(false)
        } catch (error: any) {
            setErrorState(true)
            setIsLoading(false)
            setErrorMessage({
                title: 'There was an issue locking the patient data',
                message: error.message,
            })

            setActiveMrn(activeMrn)
        }

        setChangesMade(false)
        setUpdateFields({})
    }

    const login = (msalInstance: any): Promise<UserData | null> => {
        return new Promise(async function(resolve: any, reject: any) {
            try {
              const loginResponse = await msalInstance.loginPopup({state: `${window.location.origin}/registered`});
              const userClaims: any = loginResponse.idTokenClaims
              let data: UserData | null
              if( userClaims ) {
                data = {
                    family_name: userClaims.family_name,
                    given_name: userClaims.given_name,
                    email: userClaims.emails[0],
                    state: userClaims.state,
                    country: userClaims.country,
                    postalCode: userClaims.postalCode,
                    newUser: userClaims.newUser,
                    userObjectId: userClaims.oid,
                    included: false,
                    consented: userClaims.extension_consented ? userClaims.extension_consented : false,
                    iss: userClaims.iss
                }

                localStorage.setItem('familyName', userClaims.family_name)
                localStorage.setItem('givenName', userClaims.given_name)
                localStorage.setItem('email', userClaims.emails[0])
                localStorage.setItem('userObjectId', userClaims.oid)
                localStorage.setItem('newUser', userClaims.newUser)
                localStorage.setItem('consented', userClaims.extension_consented ? userClaims.extension_consented : false)
    
                setUserData(data)
                setActiveMrn(userClaims.oid)
              } else {
                data = null
              }

              msalInstance.setActiveAccount(loginResponse.account)
              resolve(data)
          
            } catch (err) {
              const authErr = err as AuthError;
              if (authErr) {
                if (authErr.errorCode === 'user_cancelled' || authErr.errorCode === 'access_denied') {
                  setAuthError(`<div>Something went wrong during account creation or login.  The most likely causes are not filling out all required fields, or canceling two factor authentication setup.<br/><br/>Please click the Patients button and complete the registration/login process.<br/><br/>${authErr}</div>`)
				  reject({errorCode: 'user_error'})
                } else {
				  setAuthError(`<div>Seomthing went wrong during the login process.  Please try to log in again later.<br/><br/>${authErr}</div>`)
				  reject({errorCode: 'server_error'})
				}
              } else {
                  console.error({err});
                  setAuthError(`<div>Something went wrong during the login process, but it was not an authentication error.  Please try to log in again later, or contact the support team. Error:<br/><br/>${err}</div>`)
				  reject({errorCode: 'unknown_error'})
              }
            }
          }
        )
    }

    const fetchClinicalTrials = async(state: string | undefined, country: string, useStateValue: boolean, nextPageToken: string | null) => {
        setIsLoading(true)
        const url = '/empd/external/ncb/state-country'
        const query = {
            ...(nextPageToken && { nextPageToken: nextPageToken }),
            state: state,
            country: country,
            useState: useStateValue,
        }

        try {
            const response = await axios.get(`${url}?${querystring.stringify(query)}`)
            if( response.data.hasOwnProperty('totalCount') ) {
                setCountClinicalTrials(response.data.totalCount )
            } else {
                response.data.totalCount = countClinicalTrials
            }

            setClinicalTrials(response.data)
            setIsLoading(false)
        } catch( error ) {
            setClinicalTrials(null)
            setIsLoading(false)
        }
    }

    const fetchBioOptions = async(ontology: string, term: string) => {
        const url = '/empd/external/ontology'
        const query = {
            ontologies: ontology,
            term: `${term}*`,
        }

        try {
            const response = await axios.get(`${url}?${querystring.stringify(query)}`)
            setClinicalTrials(response.data)
            return response.data
        } catch( error ) {
            return []
        }
    }

    const mergeData = (data: any) => {
    
        let copyOfData: any = {} // = cloneDeep(data)
        for( const category of Object.keys(data.categories) ) {
            const result = data.categories[category].records.reduce((accumulator: any, current: any) => {
                if( accumulator.columns === undefined ) {
                    accumulator.columns = []
                }

                for( let columnIndex in current.columns ) {
                    const column = current.columns[columnIndex]
                    if( !column.hasOwnProperty('fullDatabaseFieldName') ) {
                        return accumulator
                    }
                    if( column.isMultiSelect ) {
                        let obj = accumulator.columns[columnIndex]
                        if( obj === undefined ) {
                             let keys = omit(column, ['repeatInstance', 'value'])
                            obj = keys
                            obj.value = column.hasOwnProperty('repeatInstrument') ? [{[column.repeatInstance]: [column.value]}] : column.value ? [column.value] : []
                            accumulator.columns[columnIndex] = obj
                        } else {
                            if( column.hasOwnProperty('repeatInstrument') ) {
                                if( !obj.value.hasOwnProperty(column.repeatInstance) ) {
                                    obj.value[column.repeatInstance] = [column.value]
                                } else {
                                    obj.value.push({[column.repeatInstance]: column.value})
                                }
                            } else {
                                if( column.value && !obj.value.includes(column.value) ) {
                                    obj.value.push(column.value)
                                }
                            }
                        }
                    } else {
                        if( column.hasOwnProperty('repeatInstance') ) {
                            let tt = accumulator.columns[columnIndex]
                            if( tt === undefined  ) {
                                tt = {...(omit(column, ['repeatInstance', 'value'])), value:{[column.repeatInstance]: Array.isArray(column.value) ? [column.value] : column.value}}
                            }
                        if( !tt.value.hasOwnProperty(column.repeatInstance) ) {
                                tt.value[column.repeatInstance] = Array.isArray(column.value) ? [column.value] : column.value
                            }
                            accumulator.columns[columnIndex] = tt
                        } else {
                            accumulator.columns[columnIndex] = column
                        }
                    }
                }
                return accumulator
            }, [])

            if( copyOfData && !copyOfData.hasOwnProperty('categories') ) {
                copyOfData.categories = { [category]: { records: result }}
            } else {
                copyOfData.categories[category] =  {records: result}
            }
        }
        return copyOfData
    }

	const fetchMessageContent = async () => {
		const url = '/empd_query_builder/query/content'
		try {
			setIsLoading(true)
			const response = await axios.get(url)
			response.data[0].message_of_the_day.header = response.data[0].message_of_the_day.header === null ? undefined : response.data[0].message_of_the_day.header
			response.data[1].latest.header = response.data[1].latest.header === null ? undefined : response.data[1].latest.header
			response.data[2].update.header = response.data[2].update.header === null ? undefined : response.data[2].update.header
			response.data[3].welcome_announcement.header = response.data[3].welcome_announcement.header === null ? undefined : response.data[3].welcome_announcement.header
			response.data[4].provider_message_of_the_day.header = response.data[4].provider_message_of_the_day.header === null ? undefined : response.data[4].provider_message_of_the_day.header
			response.data[5].provider_latest.header = response.data[5].provider_latest.header === null ? undefined : response.data[5].provider_latest.header
			response.data[6].provider_update.header = response.data[6].provider_update.header === null ? undefined : response.data[6].provider_update.header

			setMessagingContent(response.data)
			setIsLoading(false)
		} catch( error ) {
			console.error(error)
			setIsLoading(false)
		}
	}

	const writeMessageContent = async (type:string, content:string, header: string| undefined, showMessage:boolean) => {
		const url = 'genetic_query_builder/query/content'
		try {
			await axios.post(url, {
				type: type,
				content: content,
				header: header,
				showMessage: showMessage,
			})
		} catch(error) {
			console.error(error)
		}
	}

	const fetchProfile = async (redirectUri:string) => {
		const url = '/uem/users/current/profile'
		try {
			await getCookie(redirectUri)
			const response = await axios.get(url)
			setUser(response.data)
		} catch(error) {
			console.error(error)
		}
	}

    const sign = async () => {
        if( userData === null ) {
            return
        }

        try {
            setIsRedirecting(true)
            const env: any = await axios.post('/empd/docusign/data/envelope',{
                signerEmail: userData.email,
                signerName: userData.given_name + ' ' + userData.family_name,
                signerClientId: `12345678`,
            })
            if( env !== null ) {
                localStorage.setItem('envelopeId', env.data.envelopeId)
                setEnvelope(env.data)
                window.location.href = env.data.redirectUrl
            }
        } catch( error) {
            setIsRedirecting(false)
            //window.location.href = env.data.redirectUrl
        }
    }

    const value: ContextType = { authUser, setAuthUser, isLoading, setIsLoading, isPatientLoading, setIsPatientLoading, isRedirecting, setIsRedirecting, patientResult, setPatientResult, loadingMessage, setLoadingMessage, errorState, setErrorState, errorMessage, setErrorMessage, updateFields, setUpdateFields, userCreated, setUserCreated, changesMade, setChangesMade, fetchPatientData, addNewUser, createRedcapEntry, storeConsent, login, userData, setUserData, getCookie, writePatientData, activeMrn, setActiveMrn, writeComplete, fetchClinicalTrials, clinicalTrials, setClinicalTrials, countClinicalTrials, setCountClinicalTrials, activeSession, setActiveSession, hasBeenDiagnosed, setHasBeenDiagnosed, envelope, setEnvelope, pdfDoc, setPdfDoc, isResearchParticipant, setIsResearchParticipant, writeFlags, fetchNonParticipantData, selectedCategory, setSelectedCategory, mergeData, sign, fetchBioOptions, providerInfo, fetchProviderInfo, providerId, setProviderId, isUsPerson, setIsUsPerson, communicate, setCommunicate, openWelcomeDialog, setWelcomeDialogOpen, openDeclinedDialog, setDeclinedDialogOpen, schemaName, setSchemaName, demoTable, setDemoTable, careTable, setCareTable, idField, setIdField, fetchNamespace, conditionSet, setConditionSet, eventName, setEventName, editorState, setEditorState, openEditDialog, setEditDialogOpen, fetchMessageContent, writeMessageContent, fetchProfile, user, setUser, authError, setAuthError, openAuthErrorDialog, setAuthErrorDialogOpen, messagingContent, setMessagingContent }

    return (
        <EmpdContext.Provider value={value}>
            {children}
        </EmpdContext.Provider>
    )
}
